import { call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { push, replace } from 'redux-first-history';
import { stringify } from 'query-string';

import { safelyEncodeBase64Url } from '@dev/ee-tools';
import i18n from '@/../i18n';
import {
    getSegmentsCount,
    SegmentAddError,
    SegmentAddSuccess,
    SegmentDeleteError,
    SegmentLoadByContactError,
    SegmentLoadByContactSuccess,
    SegmentsGetNamesError,
    SegmentsGetNamesSuccess,
    SegmentsLoadError,
    SegmentsLoadHistoryTrackedErrorAction,
    SegmentsLoadHistoryTrackedSuccessAction,
    SegmentsLoadStart,
    SegmentsLoadSuccess,
    SegmentUpdateEditedSuccess,
    SegmentUpdateRuleEvaluationResult,
    SetPagination,
    SingleSegmentLoadError,
    SingleSegmentLoadSuccess,
} from '@/store/segments/actions';
import {
    SegmentAddStartAction,
    SegmentLoadByContactStartAction,
    SegmentLoadFetchStartAction,
    SegmentRuleEvaluateAction,
    SegmentsLoadHistoryTrackedStart,
    SegmentUpdateEditedAction,
    UrlSearchParams,
} from '@/store/segments/types';
import {
    addSegment,
    deleteSegment,
    getContacts,
    loadContactSegments,
    loadSegmentHistory,
    loadSegments,
    loadSingleSegment,
    updateSegment,
} from '@/services/ElasticApi';
import { HideToast, ShowToast } from '@/store/toast/actions';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { calculateOffset } from '@/services/tools/handleUrlParams';

function* getSingleSegment(action): any {
    try {
        const segment = yield call(loadSingleSegment, action.payload.name);
        yield put(SingleSegmentLoadSuccess(segment));
    } catch (e) {
        yield put(SingleSegmentLoadError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchGetSingleSegment(): any {
    yield takeLatest('@@segments/SINGLE_FETCH_START', getSingleSegment);
}

function* getSegments(action: SegmentLoadFetchStartAction): any {
    try {
        const {
            searchName = '',
            from,
            to,
            limit = DEFAULT_PER_PAGE_COUNT,
            offset = 0,
            includeHistory,
        } = action.payload;
        const loadSegmentsParams = {
            searchName,
            includeHistory,
            limit,
            offset,
            from,
            to,
        };
        const loadSegmentsResponse = yield call(loadSegments, loadSegmentsParams);

        const segments = loadSegmentsResponse.data;
        const totalCount = Number(loadSegmentsResponse.headers['x-total-count']);
        const activePage = (offset + limit) / limit;

        yield put(SetPagination({ activePage, perPage: limit, searchName }));

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage: limit,
        };
        if (searchName) {
            urlQueryParams.searchName = searchName;
        }
        yield put(replace({ search: stringify(urlQueryParams as any) }));

        yield put(SegmentsLoadSuccess(segments, totalCount));
    } catch (e) {
        yield put(SegmentsLoadError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchGetSegments(): any {
    yield takeLatest('@@segments/FETCH_START', getSegments);
}

function* getSegmentNames(): any {
    try {
        const loadSegmentsParams = {
            includeHistory: false,
            limit: 0,
            offset: 0,
        };
        const loadSegmentsResponse = yield call(loadSegments, loadSegmentsParams);

        const segments = loadSegmentsResponse.data;
        yield put(SegmentsGetNamesSuccess(segments));
    } catch (e) {
        yield put(SegmentsGetNamesError());
    }
}

function* watchGetSegmentNamess(): any {
    yield takeLatest('@@segments/FETCH_SEGMENTNAMES_START', getSegmentNames);
}

function* getContactSegments(action: SegmentLoadByContactStartAction): any {
    try {
        const contactSegments = yield call(loadContactSegments, action.payload.email);

        yield put(SegmentLoadByContactSuccess(contactSegments));
    } catch (e) {
        yield put(SegmentLoadByContactError());
        if (action.payload.showErrorToast) {
            yield put(
                ShowToast({
                    severity: 'error',
                    message: `Error: ${e?.response?.data?.Error}`,
                    duration: 6000,
                    onClose: HideToast,
                })
            );
        }
    }
}

function* watchGetContactSegments(): any {
    yield takeLatest('@@segments/LOAD_BY_CONTACT_START', getContactSegments);
}

function* deleteOneSegment(action): any {
    try {
        const { name, perPage, activePage, searchName } = action.payload;
        const count = yield select(getSegmentsCount);
        const offset = activePage ? calculateOffset(activePage, perPage, count - 1) : 0;
        yield call(deleteSegment, name);

        yield put(push(ContactsPaths.Segments));
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('segmentdeleted', { name }),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(SegmentsLoadStart({ limit: perPage, offset, searchName }));
    } catch (e) {
        yield put(SegmentDeleteError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchDeleteSegment(): any {
    yield takeLatest('@@segments/DELETE_START', deleteOneSegment);
}

function* createSegment({ payload, meta }: SegmentAddStartAction): any {
    try {
        yield call(addSegment, payload as any);
        yield put(SegmentAddSuccess(null, meta));
        yield put(push(ContactsPaths.SingleSegment.replace(':segmentname', safelyEncodeBase64Url(payload.name))));
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('segmentcreated', { name: payload?.name }),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(SegmentAddError(meta));
        console.log(e);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchCreateSegment(): any {
    yield takeLatest('@@segments/ADD_START', createSegment);
}

function* loadHistoryTrackedSegments(action: SegmentsLoadHistoryTrackedStart): any {
    try {
        const { includeHistory = true, from, to } = action.payload;

        const historyTrackedSegments = yield call(loadSegmentHistory, { includeHistory, from, to });

        yield put(SegmentsLoadHistoryTrackedSuccessAction(historyTrackedSegments));
    } catch (e) {
        yield put(SegmentsLoadHistoryTrackedErrorAction());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadHistoryTrackedSegments(): any {
    yield takeLatest('@@segments/LOAD_HISTORY_TRACKED_START', loadHistoryTrackedSegments);
}

function* evaluateSegmentRule(action: SegmentRuleEvaluateAction): any {
    try {
        const { rule } = action.payload;
        const resp = yield getContacts({ rule, limit: 1, offset: 0 });

        const ruleEvaluationResult = {
            total: parseInt(resp.headers['x-total-count'], 10) || 0,
            active: parseInt(resp.headers['x-total-active-count'], 10) || 0,
            inactive: parseInt(resp.headers['x-total-inactive-count'], 10) || 0,
            bounced: parseInt(resp.headers['x-total-bounced-count'], 10) || 0,
            engaged: parseInt(resp.headers['x-total-engaged-count'], 10) || 0,
            notConfirmed: parseInt(resp.headers['x-total-notconfirmed-count'], 10) || 0,
            unsubscribed: parseInt(resp.headers['x-total-unsubscribed-count'], 10) || 0,
            abuse: parseInt(resp.headers['x-total-abuse-count'], 10) || 0,
            stale: parseInt(resp.headers['x-total-stale-count'], 10) || 0,
            transactional: parseInt(resp.headers['x-total-transactional-count'], 10) || 0,
        };

        yield put(SegmentUpdateRuleEvaluationResult(ruleEvaluationResult));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchEvaluateRule(): any {
    yield takeLatest('@@segments/EVALUATE_RULE', evaluateSegmentRule);
}

function* updateEditedSegment(action: SegmentUpdateEditedAction): any {
    try {
        const { name, segmentPayload } = action.payload;

        yield call(updateSegment, { name, segmentPayload });
        yield put(SegmentUpdateEditedSuccess(name, segmentPayload));
        yield put(
            push(ContactsPaths.SingleSegment.replace(':segmentname', safelyEncodeBase64Url(segmentPayload.Name)))
        );
        yield put(
            ShowToast({
                severity: 'info',
                message: `${i18n.t('segmentsaved')}.`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchUpdateSegment(): any {
    yield takeLatest('@@segments/UPDATE_EDITED', updateEditedSegment);
}

const segmentSagas = [
    fork(watchGetSegments),
    fork(watchGetContactSegments),
    fork(watchDeleteSegment),
    fork(watchCreateSegment),
    fork(watchGetSingleSegment),
    fork(watchLoadHistoryTrackedSegments),
    fork(watchEvaluateRule),
    fork(watchUpdateSegment),
    fork(watchGetSegmentNamess),
];

export default segmentSagas;
